import axios from 'axios';
//import apiClient from '../axiosConfig';
import { message } from 'antd';
import config from '../config/config';

// Configuración base
//const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const AUTH_TOKEN_KEY = 'horeb_admin_token';

// Configurar axios defaults
axios.defaults.timeout = 30000;

// Crear instancia de axios personalizada
// NUEVA CONFIGURACIÓN CORRECTA:
const apiClient = axios.create({
  baseURL: config.API_URL,  // ← ESTA ES LA CLAVE
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor de request para agregar token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token && token !== 'mock-token') {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log de requests en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        data: config.data,
        params: config.params
      });
      console.log(`🔗 URL completa: ${config.baseURL}${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Interceptor de response para manejo de errores
apiClient.interceptors.response.use(
  (response) => {
    // Log de responses exitosas en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data
      });
    }
    
    return response;
  },
  (error) => {
    const { response } = error;
    
    // Log de errores en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: response?.status,
        data: response?.data,
        message: error.message
      });
    }
    
    if (response) {
      const { status, data } = response;
      
      // Manejo específico por código de estado
      switch (status) {
        case 400:
          message.error(data?.message || 'Solicitud incorrecta. Verifique los datos enviados.');
          break;
          
        case 401:
          if (data?.code === 'TOKEN_EXPIRED') {
            message.warning('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
            // Limpiar token y redirigir al login
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem('admin_user');
            window.location.href = '/login';
          } else {
            message.error(data?.message || 'No autorizado. Verifique sus credenciales.');
          }
          break;
          
        case 403:
          message.error('No tiene permisos para realizar esta acción.');
          break;
          
        case 404:
          message.error('Recurso no encontrado.');
          break;
          
        case 409:
          message.error(data?.message || 'Conflicto en los datos enviados.');
          break;
          
        case 422:
          // Errores de validación - no mostrar message general
          break;
          
        case 429:
          message.warning('Demasiadas peticiones. Intente nuevamente más tarde.');
          break;
          
        case 500:
          message.error('Error interno del servidor. Contacte al administrador.');
          break;
          
        case 503:
          message.error('Servicio no disponible temporalmente.');
          break;
          
        default:
          message.error(data?.message || 'Error inesperado en el servidor.');
      }
    } else if (error.request) {
      // Error de red
      message.error('Error de conexión. Verifique su conexión a internet.');
    } else {
      // Error de configuración
      message.error('Error inesperado. Intente nuevamente.');
    }
    
    return Promise.reject(error);
  }
);

// Funciones helper para las peticiones
const handleRequest = async (request) => {
  try {
    const response = await request;
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Clase principal de la API
class ApiService {
  
  // ==========================================
  // MÉTODOS GENERALES
  // ==========================================
  
  async get(url, config = {}) {
    return handleRequest(apiClient.get(url, config));
  }
  
  async post(url, data, config = {}) {
    return handleRequest(apiClient.post(url, data, config));
  }
  
  async put(url, data, config = {}) {
    return handleRequest(apiClient.put(url, data, config));
  }
  
  async patch(url, data, config = {}) {
    return handleRequest(apiClient.patch(url, data, config));
  }
  
  async delete(url, config = {}) {
    return handleRequest(apiClient.delete(url, config));
  }

  // ==========================================
  // MÉTODOS DE AUTENTICACIÓN
  // ==========================================
  
  async login(credenciales) {
    return this.post('/auth/login', credenciales);
  }
  
  async logout() {
    return this.post('/auth/logout');
  }
  
  async refreshToken(refreshToken) {
    return this.post('/auth/refresh', { refreshToken });
  }
  
  async verificarToken() {
    return this.get('/auth/verify');
  }

  // ==========================================
  // MÉTODOS DE CONJUNTOS
  // ==========================================
  
  // Obtener todos los conjuntos con filtros y paginación
  async obtenerConjuntos(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const url = `/conjuntos${queryParams.toString() ? `?${queryParams}` : ''}`;
    return this.get(url);
  }
  
  // Obtener conjunto específico
  async obtenerConjunto(id) {
    return this.get(`/conjuntos/${id}`);
  }
  
// Crear nuevo conjunto
async crearConjunto(datos) {
  // 🔧 DEBUG COMPLETO
  console.log('=== DEBUG API CALL ===');
  console.log('baseURL from config:', config.API_URL);
  console.log('Final URL will be:', `${config.API_URL}/conjuntos`);
  console.log('Window hostname:', window.location.hostname);
  console.log('Window protocol:', window.location.protocol);
  console.log('Data to send:', datos);
  
  return this.post('/conjuntos', datos);
}
  
  // Actualizar conjunto existente
  async actualizarConjunto(id, datos) {
    return this.put(`/conjuntos/${id}`, datos);
  }
  
  // Cambiar estado del conjunto
  async cambiarEstadoConjunto(id, datos) {
    return this.patch(`/conjuntos/${id}/estado`, datos);
  }
  
  // Eliminar conjunto (soft delete)
// Eliminar conjunto (soft delete)
async eliminarConjunto(id, motivo = '') {
  return this.delete(`/conjuntos/${id}`, {
    data: { motivo_eliminacion: motivo }
  });
}

  // Deployar conjunto automáticamente
async deployConjunto(id) {
  return this.post(`/conjuntos/${id}/deploy`);
}
  
  // Obtener módulos de un conjunto
  async obtenerModulosConjunto(id) {
    return this.get(`/conjuntos/${id}/modulos`);
  }
  
  // Actualizar módulos de un conjunto
  async actualizarModulosConjunto(id, configuracion) {
    return this.put(`/conjuntos/${id}/modulos`, configuracion);
  }
  
  // Obtener métricas de conjuntos
  async obtenerMetricasConjuntos() {
    return this.get('/conjuntos/metricas');
  }

  // ==========================================
  // MÉTODOS DEL SISTEMA
  // ==========================================
  
  // Obtener métricas del sistema
  async obtenerMetricasSistema() {
    return this.get('/sistema/metricas');
  }
  
  // Obtener estado de servicios
  async obtenerEstadoServicios() {
    return this.get('/sistema/servicios');
  }
  
  // Health check del sistema
  async healthCheck() {
    return this.get('/sistema/health');
  }

  // ==========================================
  // MÉTODOS DE ARCHIVOS
  // ==========================================
  
  // Subir archivo
  async subirArchivo(archivo, directorio = '') {
    const formData = new FormData();
    formData.append('file', archivo);
    
    if (directorio) {
      formData.append('directory', directorio);
    }
    
    return this.post('/archivos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
  
  // Eliminar archivo
  async eliminarArchivo(url) {
    return this.delete('/archivos', { data: { url } });
  }

  // ==========================================
  // MÉTODOS DE CONFIGURACIÓN
  // ==========================================
  
  // Obtener configuración del sistema
  async obtenerConfiguracion() {
    return this.get('/configuracion');
  }
  
  // Actualizar configuración
  async actualizarConfiguracion(configuracion) {
    return this.put('/configuracion', configuracion);
  }

  // ==========================================
  // MÉTODOS DE AUDITORÍA
  // ==========================================
  
  // Obtener logs de auditoría
  async obtenerLogsAuditoria(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const url = `/auditoria${queryParams.toString() ? `?${queryParams}` : ''}`;
    return this.get(url);
  }

  // ==========================================
  // MÉTODOS DE UTILIDAD
  // ==========================================
  
  // Obtener configuración de módulos disponibles
  async obtenerModulosDisponibles() {
    return this.get('/modulos-disponibles');
  }
  
  // Validar subdominio único
  async validarSubdominio(subdominio, conjuntoId = null) {
    const params = new URLSearchParams({ subdominio });
    if (conjuntoId) {
      params.append('excluir_id', String(conjuntoId));
    }
    
    return this.get(`/conjuntos/validar-subdominio?${params}`);
  }
  
  // Probar conexión a base de datos de conjunto
  async probarConexionBD(conjuntoId) {
    return this.get(`/conjuntos/${conjuntoId}/test-connection`);
  }
  
  // Exportar datos de conjunto
  async exportarDatosConjunto(conjuntoId, formato = 'excel') {
    const response = await apiClient.get(`/conjuntos/${conjuntoId}/export`, {
      params: { formato },
      responseType: 'blob'
    });
    return response.data;
  }
  
  // Generar reporte de conjuntos
  async generarReporteConjuntos(params = {}) {
    const response = await apiClient.post('/conjuntos/reporte', params, {
      responseType: 'blob'
    });
    return response.data;
  }

  // ==========================================
  // MÉTODOS PARA DESARROLLO Y DEBUG
  // ==========================================
  
  // Limpiar caché del servidor
  async limpiarCache() {
    return this.post('/sistema/clear-cache');
  }
  
  // Reiniciar servicios (solo desarrollo)
  async reiniciarServicios() {
    return this.post('/sistema/restart-services');
  }
  
  // Obtener logs del sistema
  async obtenerLogsSistema(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const url = `/sistema/logs${queryParams.toString() ? `?${queryParams}` : ''}`;
    return this.get(url);
  }
  
  // Sincronizar integración específica
  async sincronizarIntegracion(conjuntoId, tipoIntegracion) {
    return this.post(`/conjuntos/${conjuntoId}/integraciones/${tipoIntegracion}/sync`);
  }
}

// Crear instancia singleton de la API
const api = new ApiService();

// Exportar instancia y clase
export { api, ApiService };
export default api;

// Exportar funciones helper para uso directo
export const apiHelpers = {
  // Helper para manejar respuestas con loading
  withLoading: async (request, setLoading) => {
    try {
      setLoading(true);
      const result = await request;
      return result;
    } finally {
      setLoading(false);
    }
  },

  // Helper para descargar archivos
  downloadFile: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Helper para formatear errores de validación
  formatValidationErrors: (errors) => {
    return errors.reduce((acc, error) => {
      acc[error.field] = error.message;
      return acc;
    }, {});
  },

  // Helper para retry de peticiones fallidas
  retry: async (fn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn();
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
      }
    }
    throw new Error('Max retries exceeded');
  },

  // Helper para cancelar peticiones
  createCancelToken: () => {
    const source = axios.CancelToken.source();
    return {
      token: source.token,
      cancel: source.cancel
    };
  }
};
